import {
  type ApprovalMethod,
  type ApprovalMode,
  type IKnownBusinessEntity
} from "@/interfaces/api/known-business-entity.interface.ts"
import { BaseModel } from "@/models/base.model"
import {
  createCustomerInvoicingSettings,
  type CustomerInvoicingSettings
} from "@/interfaces/api/customer-invoicing-settings.interface"
import { RegisteredBusinessEntity } from "@/models/registered-business-entity.model.ts"
import { ModelFactory } from "@/factories/model-factory.ts"
import { ManDaysRegisterMode } from "@/enums/man-days-register-mode.enum.ts"
import countries from "i18n-iso-countries"
import nl from "i18n-iso-countries/langs/nl.json"

countries.registerLocale(nl)

export class KnownBusinessEntity extends BaseModel implements IKnownBusinessEntity {
  public static className = "KnownBusinessEntity" // Used for socket communication.
  public static verboseName = "Bedrijf"

  id?: string

  registeredBusinessEntity: RegisteredBusinessEntity | null

  customName?: string
  countryOfRegistration?: string
  registrationNumber?: string

  manDaysRegisterMode: ManDaysRegisterMode
  invoicingSettings: CustomerInvoicingSettings

  approvalDefaultMethod: ApprovalMethod | ""
  approvalMode: ApprovalMode
  approvalAmountRequired: boolean
  approvalFileRequired: boolean
  approvalReferenceRequired: boolean
  approvalReferenceLabel: string
  approvalReferenceFormat: string | null
  approvalReference2Required: boolean
  approvalReference2Label: string
  approvalReference2Format: string | null

  comment: string

  public constructor(knownBusinessEntity: IKnownBusinessEntity, managed = false) {
    super(knownBusinessEntity)

    this.id = knownBusinessEntity.id
    this.registeredBusinessEntity = ModelFactory.childModel(
      RegisteredBusinessEntity,
      knownBusinessEntity.registeredBusinessEntity,
      managed
    )
    this.customName = knownBusinessEntity.customName
    this.countryOfRegistration = knownBusinessEntity.countryOfRegistration
    this.registrationNumber = knownBusinessEntity.registrationNumber
    this.manDaysRegisterMode = knownBusinessEntity.manDaysRegisterMode ?? ManDaysRegisterMode.NEVER
    this.invoicingSettings = createCustomerInvoicingSettings(knownBusinessEntity.invoicingSettings)
    this.approvalDefaultMethod = knownBusinessEntity.approvalDefaultMethod ?? ""
    this.approvalMode = knownBusinessEntity.approvalMode ?? ""
    this.approvalAmountRequired = knownBusinessEntity.approvalAmountRequired ?? false
    this.approvalFileRequired = knownBusinessEntity.approvalFileRequired ?? false
    this.approvalReferenceRequired = knownBusinessEntity.approvalReferenceRequired ?? false
    this.approvalReferenceLabel = knownBusinessEntity.approvalReferenceLabel ?? ""
    this.approvalReferenceFormat = knownBusinessEntity.approvalReferenceFormat?.trim() || null
    this.approvalReference2Required = knownBusinessEntity.approvalReference2Required ?? false
    this.approvalReference2Label = knownBusinessEntity.approvalReference2Label ?? ""
    this.approvalReference2Format = knownBusinessEntity.approvalReference2Format?.trim() || null
    this.comment = knownBusinessEntity.comment
  }

  public get name(): string {
    return this.registeredBusinessEntity?.name || this.customName!
  }

  public getCountryOfRegistrationString(): string {
    let countryCode = this.countryOfRegistration
    if (this.registeredBusinessEntity) {
      countryCode = this.registeredBusinessEntity.countryOfRegistration
    }
    if (!countryCode) {
      return ""
    }
    return countries.getName(countryCode, "nl", { select: "alias" })!
  }

  public getRegistrationNumber(): string {
    let registrationNumber = this.registrationNumber
    if (this.registeredBusinessEntity) {
      registrationNumber = this.registeredBusinessEntity.registrationNumber
    }
    return registrationNumber!
  }
}
